import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal.js';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import {
  APPLICATION_PHASE_CLOSED_ERROR,
  APPLY_INACTIVE_COMPANY_ERROR,
  postJobApplications,
} from '../../../api/job-application.js';
import { subscribeMatchingWeekSettings } from '../../../api/settings.js';
import { RegisterOnboardingFinish } from './register-onboarding-finish.js';
import { RegisterOnboardingKiFeedback } from './register-onboarding-ki-feedback.js';
import { RegisterOnboardingMatchingScore } from './register-onboarding-matching-score.js';
import { RegisterOnboardingMotivation } from './register-onboarding-motivation.js';
import { RegisterOnboardingPersonalInfo } from './register-onboarding-personal-info.js';
import { RegisterOnboardingSelectOtherJobs } from './register-onboarding-select-other-jobs.js';
import { RegisterOnboardingTerms } from './register-onboarding-terms.js';

const handleApplicationError = (e: unknown) => {
  if (e instanceof Error && e.message === APPLY_INACTIVE_COMPANY_ERROR) {
    showDialog({ content: () => m.application_company_inactive_error() });
    return;
  }
  if (e instanceof Error && e.message === APPLICATION_PHASE_CLOSED_ERROR) {
    showDialog({ content: () => m.application_phase_closed_error() });
    return;
  }
  showDialog({ content: () => (e instanceof Error ? e.message : m.submit_application_unknown_error()) });
};

// Define onboarding step types for better debugging
export type OnboardingStep =
  | '1-personal-info'
  | '2-motivation'
  | '3-ki-feedback'
  | '4-terms'
  | '5-matching-score'
  | '6-select-other-jobs'
  | '7-motivation-other-jobs'
  | '8-finish';

// Mapping from step to array index
const stepToIndex: Record<OnboardingStep, number> = {
  '1-personal-info': 0,
  '2-motivation': 1,
  '3-ki-feedback': 2,
  '4-terms': 3,
  '5-matching-score': 4,
  '6-select-other-jobs': 5,
  '7-motivation-other-jobs': 6,
  '8-finish': 7,
};

// Onboarding step shown after registration + email confirmation
// Implements the wireframe: upload resume with consent, or fill form sections
export const RegisterOnboarding = stateful(() => {
  const settingsQuery = useQuery(() => subscribeMatchingWeekSettings()({ applicationPhaseClosed: 1 }), []);
  const applicationPhaseClosed = settingsQuery.value?.applicationPhaseClosed !== false;
  const settingsLoaded = !settingsQuery.loading && settingsQuery.value !== undefined;
  const [step, _setStep] = useState<OnboardingStep>('1-personal-info');
  // Steps are mounted once when first visited and then only shown/hidden. Removing or
  // replacing a mounted stateful() component corrupts the rendered DOM (the directive
  // commits outside lit's update contract), so inactive steps must stay mounted.
  const [visitedSteps] = useState(new Set<OnboardingStep>());
  const setStep = (x: OnboardingStep) => {
    _setStep(x);
    setTimeout(() => {
      const scrollContainer = document.querySelector('.overflow-y-auto');
      if (!scrollContainer) return;
      scrollContainer.scrollTop = 0;
    }, 0);
  };
  const jobIDs = routingStateSignal.value.searchParams.get('jobs')?.split(',') ?? [];
  // The originally-applied job set, captured once. Step 6 REWRITES ?jobs= to only the
  // additionally selected jobs, so steps 2 and 5 (and the step-5 application post) must
  // keep referencing the original set instead of the live param.
  const [initialJobIDs] = useState(routingStateSignal.value.searchParams.get('jobs')?.split(',').filter(Boolean) ?? []);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    const scrollContainer = document.querySelector('.drawer-content');
    if (!scrollContainer) return;
    scrollContainer.scrollTop = 0;
  }, [step]);

  if (settingsLoaded && applicationPhaseClosed) {
    return html`
      <div class="alert alert-warning shadow-lg">
        <span class="icon-[material-symbols--warning] w-6 h-6"></span>
        <div>
          <h3 class="font-bold">${m.registration_closed_title()}</h3>
          <div class="text-sm" .innerHTML=${m.registration_closed_message()}></div>
        </div>
      </div>
    `;
  }

  return html`
    ${
      stepToIndex[step] < 7
        ? html`
            <div class="flex flex-col gap-2 mb-4">
              <div class="text-sm font-medium text-base-content/70">
                ${m.register_onboarding_step_progress({ current: Math.min(stepToIndex[step] + 1, 6), total: 6 })}
              </div>
              <progress
                class="progress progress-primary w-full"
                value="${Math.min(stepToIndex[step] + 1, 6)}"
                max="6"
              ></progress>
            </div>
          `
        : nothing
    }
    ${(() => {
      const components = {
        '1-personal-info': () => RegisterOnboardingPersonalInfo({ onFinished: () => setStep('2-motivation') }),
        '2-motivation': () =>
          RegisterOnboardingMotivation({
            onFinished: () => setStep('3-ki-feedback'),
            onBack: () => setStep('1-personal-info'),
            jobIDs: initialJobIDs,
          }),
        // New Step 3: AI feedback summary and branching actions
        '3-ki-feedback': () =>
          RegisterOnboardingKiFeedback({
            onFinished: () => setStep('4-terms'),
            onBack: () => setStep('2-motivation'),
            onChangeProfile: () => setStep('1-personal-info'),
          }),
        // Step 4: Consent confirmation (moved from previous Step3)
        '4-terms': () =>
          RegisterOnboardingTerms({
            onFinished: () => setStep('5-matching-score'),
            onBack: () => setStep('3-ki-feedback'),
          }),
        // Step 5: Matching score (moved from previous Step4)
        '5-matching-score': () => html`
          ${RegisterOnboardingMatchingScore({
            jobIDs: initialJobIDs,
            onFinished: async () => {
              if (posting) return; // Guard duplicate submissions
              setPosting(true);
              try {
                await guardDialog(() => postJobApplications(initialJobIDs)(), { error: handleApplicationError });
                setPosting(false);
                setStep('6-select-other-jobs');
              } catch (e) {
                console.error('Failed to post job applications', e);
                handleApplicationError(e);
                setPosting(false);
              }
            },
            onBack: () => setStep('4-terms'),
          })}
        `,
        // Step 6: Select other jobs with score grid + modal
        '6-select-other-jobs': () =>
          RegisterOnboardingSelectOtherJobs({
            onFinished: () => setStep('7-motivation-other-jobs'),
            onBack: () => setStep('5-matching-score'),
          }),
        // Step 7: Motivations for additionally selected jobs
        '7-motivation-other-jobs': () =>
          RegisterOnboardingMotivation({
            // Deliberately the LIVE param: step 6 rewrote ?jobs= to the additionally
            // selected jobs, which is exactly the set this second pass covers.
            jobIDs,
            onBack: () => setStep('6-select-other-jobs'),
            onFinished: async () => {
              if (posting) return; // Guard duplicate submissions
              setPosting(true);
              try {
                await guardDialog(() => postJobApplications(jobIDs)(), { error: handleApplicationError });
                setPosting(false);
                setStep('8-finish');
              } catch (e) {
                console.error('Failed to post job applications', e);
                handleApplicationError(e);
                setPosting(false);
              }
            },
          }),
        // Step 8: Final confirmation + next actions
        '8-finish': () => RegisterOnboardingFinish(),
      } as const;
      visitedSteps.add(step);
      const steps = Object.keys(components) as OnboardingStep[];
      return steps.map(s => html`<div ?hidden=${s !== step}>${visitedSteps.has(s) ? components[s]() : nothing}</div>`);
    })()}
  `;
});
